import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { PageShell } from "@/components/page-shell";
import { toast } from "sonner";
import type { FieldError } from "@/lib/schemas";
import { absUrl } from "@/lib/site";

/** Inputs: a shade lighter than the page, square-shouldered, with a clear focus ring. */
const FIELD =
  "mt-1.5 block w-full rounded-sm border border-input bg-card px-3 py-3 text-base text-foreground placeholder:text-muted-foreground/80 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/25 aria-[invalid]:border-destructive aria-[invalid]:ring-2 aria-[invalid]:ring-destructive/20 sm:text-[0.9375rem]";

const LABEL = "block text-sm font-semibold text-foreground";

export const Route = createFileRoute("/contact")({
  head: () => ({
    meta: [
      { title: "Contact | LoneStarMap" },
      {
        name: "description",
        content:
          "Send LoneStarMap a question, a correction or a source we should cite. For a tick bite or a reaction, call a doctor instead.",
      },
      { property: "og:title", content: "Contact LoneStarMap" },
      { property: "og:description", content: "Get in touch with the LoneStarMap team." },
      { property: "og:url", content: absUrl("/contact") },
    ],
    links: [{ rel: "canonical", href: absUrl("/contact") }],
  }),
  component: ContactPage,
});

function ContactPage() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [company, setCompany] = useState(""); // honeypot
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<FieldError | null>(null);
  const errorId = "contact-error";

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (company) return; // bot: a person never sees the honeypot field
    // Validator (and zod with it) loads on submit rather than with the page.
    const { contactSchema, firstFieldError } = await import("@/lib/schemas");
    const parsed = contactSchema.safeParse({ name, email, message });
    if (!parsed.success) {
      const fieldError = firstFieldError(parsed.error);
      setError(fieldError);
      if (typeof document !== "undefined") {
        document.getElementById(`contact-${fieldError.field}`)?.focus();
      }
      return;
    }
    setSubmitting(true);
    let insertError: unknown = null;
    try {
      const { supabase } = await import("@/integrations/supabase/client");
      const result = await supabase.from("contact_messages").insert(parsed.data);
      insertError = result.error;
    } catch (err) {
      insertError = err;
    }
    setSubmitting(false);
    if (insertError) {
      setError({ field: "", message: "Sorry, something went wrong. Please try again." });
      toast.error("Could not send your message");
      return;
    }
    setSent(true);
    setName("");
    setEmail("");
    setMessage("");
    toast.success("Thanks. Your message was sent.");
  };

  return (
    <PageShell
      title="Get in touch"
      intro="Have feedback, a question, a source to suggest, or a report you'd like removed? Send us a note and we'll review it. For a report removal, include the approximate submission date, state, and county."
    >
      <div className="max-w-xl">
        {sent ? (
          <div className="space-y-3 border-t border-rule/70 pt-6">
            <p className="font-display text-2xl font-medium text-foreground">Message received.</p>
            <p className="text-sm text-muted-foreground">
              Thanks for reaching out. We review every note. For medical questions, please contact a
              licensed healthcare professional.
            </p>
            <button
              type="button"
              onClick={() => setSent(false)}
              className="inline-flex rounded-sm border border-foreground/30 px-5 py-3 text-sm font-semibold text-foreground hover:border-foreground"
            >
              Send another message
            </button>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="space-y-5 border-t border-rule/70 pt-6">
            <div>
              <label htmlFor="contact-name" className={LABEL}>
                Name
              </label>
              <input
                id="contact-name"
                type="text"
                required
                maxLength={100}
                value={name}
                onChange={(e) => setName(e.target.value)}
                aria-invalid={error?.field === "name" || undefined}
                aria-describedby={error?.field === "name" ? errorId : undefined}
                className={FIELD}
                placeholder="Your name"
              />
            </div>
            <div>
              <label htmlFor="contact-email" className={LABEL}>
                Email
              </label>
              <input
                id="contact-email"
                type="email"
                required
                maxLength={320}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                aria-invalid={error?.field === "email" || undefined}
                aria-describedby={error?.field === "email" ? errorId : undefined}
                className={FIELD}
                placeholder="you@example.com"
              />
              <p className="mt-1.5 text-xs text-muted-foreground">
                So we can reply if needed. Never shared.
              </p>
            </div>
            <div>
              <label htmlFor="contact-message" className={LABEL}>
                Message
              </label>
              <textarea
                id="contact-message"
                required
                maxLength={2000}
                rows={6}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                aria-invalid={error?.field === "message" || undefined}
                aria-describedby={error?.field === "message" ? errorId : undefined}
                className={FIELD}
                placeholder="What's on your mind?"
              />
              <p className="tabular mt-1.5 text-xs text-muted-foreground">{message.length}/2000</p>
            </div>
            {/* Honeypot: hidden from people and assistive tech. */}
            <div aria-hidden className="hidden">
              <label htmlFor="contact-company">Company</label>
              <input
                id="contact-company"
                type="text"
                tabIndex={-1}
                autoComplete="off"
                value={company}
                onChange={(e) => setCompany(e.target.value)}
              />
            </div>
            {error && (
              <p id={errorId} className="text-sm font-medium text-destructive" role="alert">
                {error.message}
              </p>
            )}
            <div className="flex flex-wrap items-center gap-3">
              <button
                type="submit"
                disabled={submitting}
                className="inline-flex rounded-sm bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground hover:bg-primary-dark disabled:cursor-not-allowed disabled:opacity-60"
              >
                {submitting ? "Sending…" : "Send message"}
              </button>
              <p className="text-xs text-muted-foreground">
                For medical questions, please contact a licensed healthcare professional.
              </p>
            </div>
            <p className="text-xs leading-relaxed text-muted-foreground">
              By sending this message you agree to our{" "}
              <a href="/terms" className="link">
                Terms
              </a>{" "}
              and{" "}
              <a href="/privacy" className="link">
                Privacy Policy
              </a>
              .
            </p>
          </form>
        )}
      </div>
    </PageShell>
  );
}
